import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Search, X, Clock, BookOpen, Monitor, Video, MapPin, Users, School, Eye, Target, FileText, Package, Building } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Course, CourseMode } from '@/types';

type CourseWithFamilies = Omit<Course, 'mode'> & {
  mode: string | string[];
  display_id?: string;
  course_families: {
    name: string;
  };
};
import ClassWizardLayout from './ClassWizardLayout';
import { useClassWizard } from './ClassWizardContext';

const Step4Course = () => {
  const navigate = useNavigate();
  const { state, setState } = useClassWizard();
  const [courses, setCourses] = useState<CourseWithFamilies[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<CourseWithFamilies[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedCourseDetails, setSelectedCourseDetails] = useState<CourseWithFamilies | null>(null);

  useEffect(() => {
    if (!state.selectedCourseFamily) {
      toast.error('Selecione uma família de cursos primeiro');
      navigate('/class-wizard/step4');
      return;
    }
  }, [state.selectedCourseFamily, navigate]);

  useEffect(() => {
    fetchCourses();
  }, [state.selectedCourseFamily, navigate]);

  useEffect(() => {
    applyFilters();
  }, [courses, searchTerm]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('courses')
        .select(`
          *,
          course_families (
            name
          )
        `)
        .eq('course_family_id', state.selectedCourseFamily?.id)
        .or('duration_onlinesync.not.is.null,duration_inperson.not.is.null');

      if (error) throw error;
      
      setCourses(data || []);
      setFilteredCourses(data || []);
    } catch (error: any) {
      toast.error('Erro ao carregar cursos', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (!searchTerm) {
      setFilteredCourses(courses);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = courses.filter(course => 
      course.name.toLowerCase().includes(term) ||
      (course.description && course.description.toLowerCase().includes(term))
    );
    
    setFilteredCourses(filtered);
  };

  const handleSelectCourse = (course: Course) => {
    setState(prev => ({ ...prev, selectedCourse: course }));
    navigate('/class-wizard/step6');
  };

  const getModeBadge = (mode: CourseMode) => {
    switch (mode) {
      case 'onlineAsync':
        return <Badge variant="outline" className="bg-blue-100 text-blue-800">Online Assíncrono</Badge>;
      case 'onlineSync':
        return <Badge variant="outline" className="bg-green-100 text-green-800">Online Síncrono</Badge>;
      case 'in-person':
        return <Badge variant="outline" className="bg-purple-100 text-purple-800">Presencial</Badge>;
      default:
        return <Badge variant="outline">Desconhecido</Badge>;
    }
  };

  const getModeBadges = (modes: string | string[]) => {
  const modeArray = Array.isArray(modes) ? modes : [modes];
  
  return modeArray.map((singleMode, index) => {
    switch (singleMode) {
      case 'onlineAsync':
        return (
          <Badge key={index} variant="outline" className="bg-blue-50 text-blue-700 hover:bg-blue-50 border-blue-200">
            <Video className="h-3 w-3 mr-1" />
            Assíncrono
          </Badge>
        );
      case 'onlineSync':
        return (
          <Badge key={index} variant="outline" className="bg-purple-50 text-purple-700 hover:bg-purple-50 border-purple-200">
            <Monitor className="h-3 w-3 mr-1" />
            Síncrono
          </Badge>
        );
      case 'inPerson':
        return (
          <Badge key={index} variant="outline" className="bg-green-50 text-green-700 hover:bg-green-50 border-green-200">
            <MapPin className="h-3 w-3 mr-1" />
            Presencial
          </Badge>
        );
      default:
        return null;
    }
  });
};

const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h${remainingMinutes}min`;
  };

  const getDurationInfo = (course: Course) => {
    const durations = [];
    
    if (course.duration_onlinesync) {
      durations.push({
        icon: <Monitor className="h-4 w-4" />,
        prefix: "S",
        duration: formatDuration(course.duration_onlinesync)
      });
    }
    
    if (course.duration_inperson) {
      durations.push({
        icon: <School className="h-4 w-4" />,
        prefix: "P",
        duration: formatDuration(course.duration_inperson)
      });
    }
    
    return durations;
  };

  return (
    <ClassWizardLayout
      currentStep={5}
      title="Selecionar Curso"
      description={`Escolha um curso da família "${state.selectedCourseFamily?.name}"`}
      nextDisabled={true}
    >
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Buscar cursos..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {searchTerm && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSearchTerm('')}
              title="Limpar busca"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="outline"
            onClick={fetchCourses}
            disabled={loading}
          >
            Atualizar
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array(4).fill(0).map((_, i) => (
              <Card key={i} className="relative h-full overflow-hidden">
                <div className="aspect-video">
                  <Skeleton className="h-full w-full" />
                </div>
                <CardContent className="p-4 space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-20" />
                    <Skeleton className="h-6 w-20" />
                  </div>
                  <Skeleton className="h-9 w-full mt-2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredCourses.length === 0 ? (
          <Card className="shadow-sm">
            <CardContent className="p-8 text-center">
              <BookOpen className="h-12 w-12 mx-auto text-muted-foreground opacity-50 mb-4" />
              <h3 className="text-lg font-extrabold mb-2">Nenhum curso encontrado</h3>
              <p className="text-muted-foreground mb-4">Não encontramos nenhum curso com os filtros selecionados.</p>
              <Button onClick={() => setSearchTerm('')}>Limpar busca</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredCourses.map((course) => (
              <Card key={course.id} className="group relative h-full overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                {/* Course image (thumbnail or cover) */}
                <div className="relative aspect-video bg-gradient-to-br from-[#3b65f5]/10 to-[#ff4a8f]/10 dark:from-[#3b65f5]/20 dark:to-[#ff4a8f]/20 overflow-hidden">
                  {course.thumbnail_url || course.cover_url ? (
                    <img 
                      src={course.thumbnail_url || course.cover_url} 
                      alt={course.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#3b65f5]/10 via-[#ff4a8f]/5 to-[#c8e600]/10 dark:from-[#3b65f5]/20 dark:via-[#ff4a8f]/20 dark:to-[#c8e600]/20">
                      <BookOpen className="h-12 w-12 text-[#3b65f5] dark:text-[#3b65f5]/80" />
                    </div>
                  )}
                  
                  {/* Category label over the image */}
                  <div className="absolute top-3 left-3">
                    <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-extrabold shadow-sm">
                      {course.course_families?.name || 'Geral'}
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-4 space-y-3">
                  {/* Course name and description */}
                  <div>
                    <h3 className="font-extrabold text-lg leading-tight mb-1">{course.name}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-4">{course.description}</p>
                  </div>

                  {/* Duration info */}
                  <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                    {getDurationInfo(course).map((info, index) => (
                      <div key={index} className="flex items-center gap-1.5">
                        {info.icon}
                        <span className="font-extrabold">{info.prefix}:</span>
                        <span>{info.duration}</span>
                      </div>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      className="flex-1"
                      variant={state.selectedCourse?.id === course.id ? "default" : "outline"}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectCourse(course);
                      }}
                    >
                      {state.selectedCourse?.id === course.id ? "Selecionado" : "Selecionar"}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCourseDetails(course);
                        setIsDetailsOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Course Details Modal */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 border-0">
          {selectedCourseDetails && (
            <div className="space-y-6">
              {/* Hero Section */}
              <div className="relative h-72">
                {selectedCourseDetails.cover_url ? (
                  <img 
                    src={selectedCourseDetails.cover_url} 
                    alt={selectedCourseDetails.name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#3b65f5] via-[#ff4a8f] to-[#c8e600] flex items-center justify-center">
                    <BookOpen className="h-24 w-24 text-white/30" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex gap-2 mb-3">
                    <Badge variant="secondary" className="bg-[#ff4a8f] hover:bg-[#ff4a8f]/80 text-white border-0">
                      Online Síncrono
                    </Badge>
                    <Badge variant="secondary" className="bg-[#c8e600] hover:bg-[#c8e600]/80 text-white border-0">
                      Presencial
                    </Badge>
                    <Badge variant="secondary" className="bg-[#3b65f5] hover:bg-[#3b65f5]/80 text-white border-0">
                      Online Assíncrono
                    </Badge>
                  </div>
                  <h2 className="text-2xl font-extrabold mb-2">{selectedCourseDetails.name}</h2>
                  <div className="flex gap-2">
                    {selectedCourseDetails.course_families?.name && (
                      <Badge variant="secondary" className="bg-white/20 text-white">
                        {selectedCourseDetails.course_families.name}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Course Content */}
              <div className="px-6 pb-6 space-y-6">
                {/* Overview */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Eye className="h-5 w-5 text-blue-500" />
                      <CardTitle>Visão Geral</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedCourseDetails.overview && (
                      <div>
                        <h4 className="font-extrabold mb-2">Sobre o Curso</h4>
                        <p className="text-sm text-muted-foreground whitespace-pre-line">
                          {selectedCourseDetails.overview}
                        </p>
                      </div>
                    )}
                    {selectedCourseDetails.description && (
                      <div>
                        <h4 className="font-extrabold mb-2">Descrição Detalhada</h4>
                        <p className="text-sm text-muted-foreground whitespace-pre-line">
                          {selectedCourseDetails.description}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Learning Goals */}
                {selectedCourseDetails.learning_goals && (
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-green-500" />
                        <CardTitle>Objetivos de Aprendizagem</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {selectedCourseDetails.learning_goals.split('\n').map((goal, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <div className="h-2 w-2 rounded-full bg-green-500 mt-2" />
                            <p className="text-sm text-muted-foreground">{goal}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Requirements */}
                {selectedCourseDetails.requirements && (
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <Package className="h-5 w-5 text-purple-500" />
                        <CardTitle>Requisitos</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {selectedCourseDetails.requirements.split('\n').map((req, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <div className="h-2 w-2 rounded-full bg-purple-500 mt-2" />
                            <p className="text-sm text-muted-foreground">{req}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}


              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ClassWizardLayout>
  );
};

export default Step4Course; 